import React, { useEffect, useState } from 'react';
import ReactTooltip from 'react-tooltip';
import { Button, Card, Form, InputGroup, Spinner } from 'react-bootstrap';
import { IconButton } from '@material-ui/core';
import SaveIcon from '@material-ui/icons/Save';
import DeleteIcon from '@material-ui/icons/Delete';
import ArrowBackIosIcon from '@material-ui/icons/ArrowBackIos';

import { saveProfile } from '../../services/firebase';
import showToastMessage from '../../util/showToastMessage';
import { pastePlainTextOnly } from '../../util/pastePlainText';
import { SpinnerText } from '../../components/custom-styled-components/CommonStyledComponents';

interface IProfileChange {
  index: number;
  title: string;
}

export default function Profile({ closeProfile, onToggleProfile }: any) {
  const [profile, setProfile]: any = useState(null);
  const [activeProfile, setActiveProfile] = useState<number | null>(null);
  const [profileHeadline, setProfileHeadline] = useState('');
  const [showSpinner, setShowSpinner] = useState(false);
  const [changedProfileId, setChangedProfileId] = useState<number | null>(null);
  const [changedProfile, setChangedProfile] = useState<IProfileChange | null>(null);
  useEffect(() => {
    getProfileData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function getProfileData() {
    try {
      setShowSpinner(true);

      const response: any = await saveProfile({ type: 'getAll' });

      if (hasValidProfileData(response)) {
        const { id, payload: { profileData = [] } } = response.data[0];

        setProfile({ id, profileData });

        const activeIndex = findActiveProfileIndex(profileData);
        setActiveProfile(activeIndex);
      }
    } catch (error) {
      if (error instanceof Error) {
        handleErrorMessage(error);
      }
    } finally {
      setShowSpinner(false);
    }
  }

  function hasValidProfileData(response: any) {
    return response && response.data && response.data.length > 0;
  }

  function findActiveProfileIndex(profileData: any) {
    const activeIndex = profileData.findIndex((item: any) => item.enable === true);
    return activeIndex >= 0 ? activeIndex : null;
  }

  async function addProfile() {
    if (!profileHeadline) {
      return;
    }

    setShowSpinner(true);

    try {
      const response = await (profile
        ? updateExistingProfile()
        : createNewProfile());

      if (response) {
        handleAddProfileSuccess();
      }
    } catch (error) {
      if (error instanceof Error) {
        handleErrorMessage(error);
      }
    } finally {
      setShowSpinner(false);
    }
  }

  async function updateExistingProfile() {
    return await saveProfile({
      type: 'update',
      id: profile.id,
      record: { profileData: [...profile.profileData, { title: profileHeadline, enable: false }] }
    });
  }

  async function createNewProfile() {
    return await saveProfile({
      type: 'create',
      record: { profileData: [{ title: profileHeadline, enable: true }] }
    });
  }

  function handleAddProfileSuccess() {
    showToastMessage({ type: 'success', actionType: 'create', title: 'Data created successfully.', description: `Created profile: ${profileHeadline}` });
    setProfileHeadline('');
    getProfileData();
  }

  async function updateProfile(profileIndex: number) {
    if (!profile || !profile.profileData) {
      return;
    }

    setChangedProfileId(profileIndex);

    const updatedProfileData = profile.profileData.map((item: any, i: number) => ({
      title: item.title,
      enable: profileIndex === i ? !item.enable : false
    }));

    const profileTitle = updatedProfileData.find((item: any, i: number) => item && profileIndex === i);
    setActiveProfile(profileTitle.enable ? profileIndex : null);

    const response: any = await saveProfile({
      type: 'update',
      id: profile.id,
      record: { profileData: updatedProfileData }
    });

    if (response) {
      handleUpdateProfileSuccess(updatedProfileData, profileTitle.enable ? profileTitle.title : null);
    }

    setChangedProfileId(null);
  }

  function handleUpdateProfileSuccess(updatedProfileData: any, enabledProfileTitle: string | null) {
    showToastMessage({ type: 'success', actionType: 'update', title: 'Data updated successfully.', description: `${enabledProfileTitle ? 'Activated profile: ' + enabledProfileTitle : 'Profile deactivated'}`});
    setProfile({ ...profile, profileData: updatedProfileData });
    onToggleProfile(enabledProfileTitle);
  }

  const updateProfileTitle = (titleIndex: number, updatedTitle: string) => {
    setChangedProfile({
      index: titleIndex,
      title: updatedTitle
    });
  };

  const saveUpdatedProfileTitle = async () => {
    if (!changedProfile || !profile || !profile.profileData) {
      return;
    }

    const { index, title } = changedProfile;
    const currentProfileData = profile.profileData[index];

    if (!currentProfileData || !currentProfileData.title) {
      return;
    }

    currentProfileData.title = title;

    setChangedProfileId(index);

    try {
      const response = await saveProfile({
        type: 'update',
        id: profile.id,
        record: { profileData: profile.profileData }
      });

      if (response) {
        onToggleProfile(title);
        showToastMessage({ type: 'success', actionType: 'update', title: 'Data updated successfully.', description: `Updated profile: ${title}`});
      }
    } catch (error) {
      if (error instanceof Error) {
        handleErrorMessage(error);
      }
    } finally {
      setChangedProfile(null);
      setChangedProfileId(null);
    }
  };

  const deleteProfile = async (index: number) => {
    if (!profile || !profile.profileData || !profile.profileData[index]) {
      return;
    }

    try {
      setChangedProfileId(index);

      const updatedProfileData = profile.profileData.filter((_: any, i: number) => i !== index);

      const response: any = await saveProfile({
        type: 'update',
        id: profile.id,
        record: { profileData: updatedProfileData }
      });

      if (response) {
        showToastMessage({ type: 'success', actionType: 'delete', title: 'Data deleted successfully.', description: `Deleted profile: ${profile.profileData[index].title}`});
        if (activeProfile === index) {
          setActiveProfile(null);
          onToggleProfile(null);
        }

        profile.profileData.splice(index, 1);
      }
    } catch (error) {
      if (error instanceof Error) {
        handleErrorMessage(error);
      }
    } finally {
      setChangedProfileId(null);
    }
  };

  // Handle catch error
  function handleErrorMessage(error: Error) {
    const { name, message } = error;
    showToastMessage({ type: 'error', title: name, description: message });
  }

  return (
    <div className="cmn-saved-prompts-drawer">
      <div className="cmn-saved-prompts-title-bar">
          <button id="back-btn" className="btn btn-outline-light" onClick={() => closeProfile(false)}><ArrowBackIosIcon fontSize="small"/>Back</button>
        <div className="title-head title-head-fullscreen">
          <h2>
            Profile
          </h2>
        </div>

        <div className="icon-container">
         <button className="close-library" onClick={() => closeProfile(false)}>X</button>
        </div>
      </div>
      <div className="cmn-saved-prompts-body">
        <InputGroup className="p-3 saved-library-input-group">
            <Form.Control
              className="rounded"
              placeholder="Profile Headline"
              name="profileHeadline"
              value={profileHeadline}
              onChange={(e: any) => setProfileHeadline(e.target.value)}
            />
             <Button variant="outline-secondary" size="sm" className="ml-2 profile-add-btn" id="profile-headline" onClick={addProfile} disabled={showSpinner || !!!profileHeadline}>
              Add Profile
            </Button>
        </InputGroup>

        <div className="prompts-list profile-list">
          {
            showSpinner ? (
              <div className="text-center quote-without-author" >
                <Spinner animation="border" variant="secondary" />
                <SpinnerText>Loading Profiles...</SpinnerText>
              </div>
            )
            :
            <>
              <ReactTooltip id="title-tooltip" className="cmn-saved-prompts-tooltip" place="bottom" effect="solid" />
              {
                activeProfile != null ?
                  (profile && profile.profileData[activeProfile] && profile.profileData[activeProfile].title &&
                    <div>
                      <h6 className="mb-3 text-white current-profile-label px-3">
                        Current enabled Profile: <span data-tip={profile.profileData[activeProfile].title} data-for="current-profile-tooltip">{ profile.profileData[activeProfile].title }</span>
                      </h6>
                      <ReactTooltip id="current-profile-tooltip" className="cmn-saved-prompts-tooltip" place="bottom" effect="solid" />
                    </div>)
                  :
                  (<h6 className="mb-3 text-white current-profile-label px-3">Current enabled Profile: No Profile selected</h6>)
              }
              {
                profile && profile.profileData && profile.profileData.length ? profile.profileData.map((item: any, i: number) => (
                  <Card
                    key={i}
                    text="white"
                    className="mb-2 mr-1 profile-card"
                  >
                    <Card.Body>
                      <Card.Title
                        data-tip= {item.title}
                        data-for="title-tooltip"
                        className={`fs-15 profile-label ${changedProfileId === i ? 'w-100 border border-3 rounded border-dark name' : 'w-auto mr-auto'} profile-title`}
                        contentEditable={true}
                        suppressContentEditableWarning={true}
                        onInput={(e: any) => updateProfileTitle(i, e.currentTarget.textContent)}
                        onPaste={pastePlainTextOnly}
                      >
                        { item.title }
                      </Card.Title>
                      { changedProfileId === i && <div><Spinner animation="border" variant="primary" className="mr-2" /></div> }
                      {
                        changedProfile && changedProfile.index === i && (
                          <IconButton
                            data-tip="Save"
                            data-for="title-tooltip"
                            onClick={(e: any) => saveUpdatedProfileTitle()}
                            aria-label="fav-icon"
                            component="span"
                            className="mx-2"
                            size="small"
                          >
                            <SaveIcon style={{color: '#0f6ecd'}} />
                          </IconButton>
                        )
                      }
                      <IconButton
                        data-tip="Delete Profile"
                        data-for="title-tooltip"
                        onClick={() => deleteProfile(i)}
                        aria-label="fav-icon"
                        component="span"
                        >
                        <DeleteIcon fontSize="large" style={{color: '#f92626ed'}} />
                      </IconButton>
                      <label className="profile-toggle-switch">
                        <input type="checkbox" checked={item.enable} onChange={() => updateProfile(i)}/>
                        <span className="slider round" data-for="title-tooltip" data-tip={item.enable ? 'Disable Profile' : 'Activate Profile'}></span>
                      </label>
                    </Card.Body>
                  </Card>
                ))
                : (
                !showSpinner && <div className="d-flex justify-content-center mt-5 h3 text-light">
                  No Data found
                </div>
                )
              }
            </>
          }
        </div>
      </div>
    </div>
  );
}
